import React, { useState, useEffect } from 'react';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useOptionalNavigation } from '../contexts/NavigationContext';
import { DataService } from '../services/dataService';
import { ContextualChatButton } from '../components/ContextualChatButton';
import type { RestDay, MobilityRoutine, Workout } from '../types'; // 🆕 FEB 2 (Bug #61): Add Workout for rest day activities
// Removed: import { Glasses } from 'lucide-react'; - Now using REST badge
import { CmpBootomnavCoach as ProperNavCoach } from '../anima/Navigation Component/src/components/CmpBootomnavCoach/CmpBootomnavCoach.jsx';

/**
 * 🛌 REST DAY DETAIL PAGE (Simplified - Jan 23, 2026)
 * 
 * SCOPED STRUCTURE:
 * 1. Header - matches WorkoutDetail exactly
 * 2. Briefing - short description (LLM-customized to goal/framework)
 * 3. Linked Mobility - expandable drawer if poses exist
 * 4. Science of Rest - customized to user's goal/framework (from restDay.notes)
 */

interface RestDayDetailProps {
  restDayId?: string;
}

const RestDayDetail: React.FC<RestDayDetailProps> = ({ restDayId: propRestDayId }) => {
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🛌 REST DAY DETAIL: Component render started');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  const { id } = useLocalSearchParams<{ id: string }>();
  const restDayId = propRestDayId || id;
  
  console.log('🛌 REST DAY DETAIL: restDayId =', restDayId);
  
  const router = useRouter();
  
  // 🔧 FIX (Jan 29, 2026): Defensive navigation hook usage
  // Problem: When navigating between rest days, component re-mounts and NavigationProvider
  // context is temporarily unavailable → throws "useNavigation must be used within provider"
  // Solution: Use try-catch with fallback to React Router's useNavigate
  // 2026-09-17: was a try/catch around useNavigation() — a hook inside a try
  // block is a Rules-of-Hooks violation. useOptionalNavigation() is the same
  // contract (null when no provider) called unconditionally.
  const navigationContext = useOptionalNavigation();
  
  const goBack = navigationContext?.goBack || (() => router.back());
  const getCurrentSource = navigationContext?.getCurrentSource || (() => 'unknown');
  
  const [restDay, setRestDay] = useState<RestDay | null>(null);
  const [linkedMobility, setLinkedMobility] = useState<MobilityRoutine[]>([]);
  const [linkedWorkouts, setLinkedWorkouts] = useState<Workout[]>([]); // 🆕 FEB 2 (Bug #61): Rest day workouts
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    console.log('🛌 REST DAY DETAIL: useEffect running, restDayId =', restDayId);
    
    if (!restDayId) {
      console.error('❌ REST DAY DETAIL: No restDayId provided!');
      return;
    }

    const loadRestDayData = async () => {
      console.log('🔄 REST DAY DETAIL: Starting data load...');
      try {
        const allData = await DataService.getAllData();
        console.log('✅ REST DAY DETAIL: getAllData() succeeded, restDays count:', allData.restDays?.length);
        
        // Find rest day
        const restDayData = allData.restDays.find((r: RestDay) => r.id === restDayId);
        if (!restDayData) {
          console.error('Rest day not found:', restDayId);
          return;
        }
        
        setRestDay(restDayData);
        
        // 🔧 MAR 13: Load linked mobility with context='rest_day' ONLY
        // Must match PlanDetail's narrower filter — only context='rest_day' gets nested
        const dateStr = restDayData.scheduledDate ? new Date(restDayData.scheduledDate).toISOString().split('T')[0] : '';
        const matchingMobility = allData.mobilityRoutines.filter((m: MobilityRoutine) => {
          const mobilityDateStr = m.scheduledDate ? new Date(m.scheduledDate).toISOString().split('T')[0] : '';
          return mobilityDateStr === dateStr && (m as any).context === 'rest_day';
        });
        setLinkedMobility(matchingMobility);

        // 🔧 MAR 13: Load linked workouts with context='rest_day' ONLY
        const matchingWorkouts = allData.workouts.filter((w: Workout) => {
          const workoutDateStr = w.scheduledDate ? new Date(w.scheduledDate).toISOString().split('T')[0] : '';
          return workoutDateStr === dateStr && (w as any).context === 'rest_day';
        });
        setLinkedWorkouts(matchingWorkouts);

      } catch (error) {
        console.error('Error loading rest day data:', error);
      }
    };

    loadRestDayData();
  }, [restDayId]);

  if (!restDay) {
    console.log('🛌 REST DAY DETAIL: No rest day data yet, showing loading...');
    return (
      <div style={{ 
        padding: '24px', 
        textAlign: 'center',
        minHeight: '100vh',
        backgroundColor: 'white'
      }}>
        <p style={{ color: 'var(--coloron-surface600)' }}>Loading rest day...</p>
      </div>
    );
  }
  
  console.log('🛌 REST DAY DETAIL: Rest day loaded, rendering full component:', restDay.title);

  // 🔧 FIX FEB 4, 2026: Use NavigationContext goBack() ONLY for stateful overlay
  const handleSmartBack = () => {
    console.log('🔙 RestDayDetail: handleSmartBack called');
    const source = getCurrentSource();
    console.log('🔙 RestDayDetail: source =', source);
    
    // Special case: If came from plan-detail, navigate to that plan (route-based)
    if (source === 'plan-detail') {
      console.log('🔙 RestDayDetail: Going back to plan-detail');
      if (restDay.planId) {
        router.push(`/plan/${restDay.planId}`);
      } else if (navigationContext) {
        navigationContext.goBack();
      }
      return;
    }
    
    // 🔧 CRITICAL: MyPath uses stateful overlay pattern (line 1984)
    // Must use goBack() to reset navigationState, NOT navigate()
    if (navigationContext) {
      console.log('🔙 RestDayDetail: Using NavigationContext.goBack() for stateful overlay');
      
      // Dispatch event to MyPath to set viewMode/entityFilter
      if (source === 'today' || source === 'browse') {
        console.log('🔙 RestDayDetail: Dispatching navigateBackToMyPath event for source:', source);
        window.dispatchEvent(new CustomEvent('navigateBackToMyPath', { 
          detail: { source } 
        }));
      }
      
      // Reset NavigationContext state (closes detail overlay)
      navigationContext.goBack();
      console.log('🔙 RestDayDetail: NavigationContext.goBack() called - detail should close');
    } else {
      console.log('🔙 RestDayDetail: No NavigationContext - falling back to router.back()');
      // Fallback for route-based navigation
      router.back();
    }
  };

  const handleToggleComplete = async () => {
    if (isCompleting) return;
    
    setIsCompleting(true);
    try {
      const result = restDay.status === 'completed' ?
        await DataService.markRestDayIncomplete(restDay.id) :
        await DataService.markRestDayComplete(restDay.id);
      
      if (result.success) {
        setRestDay({
          ...restDay,
          status: restDay.status === 'completed' ? 'active' : 'completed',
          completedAt: restDay.status === 'completed' ? undefined : new Date()
        });
      }
    } catch (error) {
      console.error('Error toggling rest day:', error);
    } finally {
      setIsCompleting(false);
    }
  };

  const handleNavigation = (tab: string) => {
    switch (tab) {
      case 'cedar':
        router.push('/chat');
        break;
      case 'my-path':
      case 'mypath':
        router.push('/mypath?resetToToday=true');
        break;
      case 'momentum':
      case 'insights':
        router.push('/insights');
        break;
      case 'settings':
        router.push('/settings');
        break;
      default:
        router.push('/chat');
        break;
    }
  };

  const getRestDayTypeLabel = (type: string): string => {
    switch (type) {
      case 'active-recovery': return 'Active Recovery';
      case 'complete': return 'Full Rest';
      case 'recovery': return 'Recovery';
      case 'complete_rest': return 'Complete Rest';
      default: return 'Rest';
    }
  };

  const isCompleted = restDay.status === 'completed';

  return (
    <div className="h-screen overflow-hidden bg-gray-50 max-w-md mx-auto flex flex-col">
      {/* 🛌 JAN 23, 2026: Header - Match WorkoutDetail structure exactly */}
      <div className="bg-white border-b border-gray-200 px-4 sticky top-0 z-10 flex-shrink-0"
           style={{ 
             paddingTop: 'max(12px, env(safe-area-inset-top))',
             paddingBottom: '12px'
           }}>
        {/* Row 1: Back button + Right actions (checkmark + chat) */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={handleSmartBack}
            className="flex items-center text-gray-600 hover:text-gray-900 p-1 -ml-1 flex-shrink-0"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back</span>
          </button>
          
          {/* Right actions - Match workout card styling */}
          <div className="flex items-center gap-3">
            {/* Checkmark */}
            <div 
              onClick={handleToggleComplete}
              className="cursor-pointer flex-shrink-0"
              title={isCompleted ? 'Mark incomplete' : 'Mark complete'}
            >
              {isCompleted ? (
                <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex items-center justify-center">
                </div>
              )}
            </div>
            
            {/* Chat button */}
            <ContextualChatButton
              context={{
                type: 'rest_day',
                title: restDay.title || getRestDayTypeLabel(restDay.type),
                description: restDay.description || '',
                entity_id: restDay.id,
                metadata: {
                  rest_type: restDay.type,
                  scheduled_date: restDay.scheduledDate,
                  linked_mobility_count: linkedMobility.length,
                  linked_workout_count: linkedWorkouts.length // 🆕 FEB 2 (Bug #61)
                },
                action: 'discuss'
              }}
              variant="clean"
              size="small"
            />
          </div>
        </div>
        
        {/* Row 2: Title with REST badge */}
        <div className="flex items-center gap-3 mb-2">
          {/* 🛌 REST Badge (matches card pattern) */}
          <div 
            className="flex-shrink-0 flex items-center justify-center"
            style={{
              width: '48px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: 'rgba(46, 139, 139, 0.15)'
            }}
          >
            <span 
              className="text-xs font-bold"
              style={{ 
                color: 'var(--cedar-accent-sky)',
                letterSpacing: '0.5px'
              }}
            >
              REST
            </span>
          </div>
          
          {/* Title */}
          <h1 className="text-xl font-bold text-gray-900 flex-1">
            Rest Day: {restDay.title || getRestDayTypeLabel(restDay.type)}
          </h1>
        </div>

        {/* Row 3: Type badge */}
        <div className="flex items-center gap-2">
          <span 
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
            style={{
              backgroundColor: 'rgba(46, 139, 139, 0.15)',
              color: '#2E8B8B'
            }}
          >
            {getRestDayTypeLabel(restDay.type)}
          </span>
        </div>
      </div>

      {/* 🛌 JAN 23, 2026: Scrollable content area */}
      <div 
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{ 
          paddingBottom: 'calc(82px + env(safe-area-inset-bottom, 0px))',
          WebkitOverflowScrolling: 'touch'
        }}
      >

        {/* 1. BRIEFING - Short description (LLM-customized to goal/framework) */}
        {restDay.description && (
          <div className="mx-4 mt-4 mb-6">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-sm leading-relaxed text-gray-700">
                {restDay.description}
              </p>
            </div>
          </div>
        )}

        {/* 🆕 2. SUGGESTED ACTIVITIES - Bulleted list (active recovery guidance) */}
        {restDay.activities && restDay.activities.length > 0 && (
          <div className="mx-4 mb-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">
              Suggested Activities
            </h2>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <ul className="space-y-2">
                {restDay.activities.map((activity: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                    <span 
                      className="font-bold mt-0.5" 
                      style={{ color: 'var(--cedar-accent-sky)' }}
                    >
                      •
                    </span>
                    <span>{activity}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* 3. LINKED ACTIVITIES - Expandable drawer (Workouts + Mobility) */}
        {/* 🆕 FEB 2, 2026 (Bug #61): Show BOTH workouts and mobility as optional activities */}
        {(linkedWorkouts.length > 0 || linkedMobility.length > 0) && (
          <div className="mx-4 mb-6">
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <summary className="cursor-pointer p-4 hover:bg-gray-50 transition-colors flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      {linkedWorkouts.length + linkedMobility.length} Optional {linkedWorkouts.length + linkedMobility.length === 1 ? 'Activity' : 'Activities'}
                    </h3>
                    <p className="text-xs text-gray-600">Tap to view details</p>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              
              <div className="p-4 pt-2 border-t border-gray-100 space-y-3">
                {/* Linked Workouts (recovery activities like walks) */}
                {linkedWorkouts.map(workout => (
                  <div
                    key={workout.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      router.push(`/workout/${workout.id}?source=rest-day-detail&parentRestDayId=${restDay.id}`);
                    }}
                    className="p-3 bg-blue-50 rounded-lg border border-blue-100 cursor-pointer hover:bg-blue-100 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">{workout.title}</h4>
                        {workout.duration && (
                          <p className="text-xs text-gray-600 mt-1">
                            {workout.duration} min{workout.activityType && ` • ${workout.activityType}`}
                          </p>
                        )}
                      </div>
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ))}
                
                {/* Linked Mobility (yoga, stretching, foam rolling) */}
                {linkedMobility.map(mobility => (
                  <div
                    key={mobility.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      router.push(`/mobility/${mobility.id}?source=rest-day-detail&parentRestDayId=${restDay.id}`);
                    }}
                    className="p-3 bg-teal-50 rounded-lg border border-teal-100 cursor-pointer hover:bg-teal-100 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">{mobility.title}</h4>
                        {mobility.duration_minutes && (
                          <p className="text-xs text-gray-600 mt-1">
                            {mobility.duration_minutes} min
                            {mobility.exercises && mobility.exercises.length > 0 && ` • ${mobility.exercises.length} poses`}
                          </p>
                        )}
                      </div>
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}

        {/* 🆕 4. THE SCIENCE OF REST - Collapsible bulleted benefits (from coaching_notes.why) */}
        {restDay.notes && (
          <div className="mx-4 mb-6">
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <summary className="cursor-pointer p-4 hover:bg-gray-50 transition-colors flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h2 className="text-base font-semibold text-gray-900">
                    The Science of Rest
                  </h2>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 border-t border-gray-100">
                {/* Parse and render bullets properly */}
                {(() => {
                  const text = restDay.notes;

                  // Check if text contains bullet points (• character)
                  if (text.includes('•')) {
                    // Split by bullet character
                    const parts = text.split('•').filter(part => part.trim());

                    return (
                      <ul className="space-y-2 mt-3">
                        {parts.map((bullet, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <span
                              className="font-bold mt-0.5"
                              style={{ color: 'var(--cedar-accent-sky)' }}
                            >
                              •
                            </span>
                            <span>{bullet.trim()}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  }

                  // No bullets - render as paragraph
                  return (
                    <p className="text-sm leading-relaxed text-gray-700 mt-3">
                      {text}
                    </p>
                  );
                })()}
              </div>
            </details>
          </div>
        )}

      </div>

      {/* Bottom Navigation */}
      <div 
        className="navigation-container fixed bottom-0 left-1/2 transform -translate-x-1/2 bg-white border-t border-gray-200 z-50"
        style={{ width: '100%', maxWidth: '448px' }}
      >
        <ProperNavCoach 
          activeTab="mypath"
          className=""
          onClickTab={handleNavigation}
        />
      </div>
    </div>
  );
};

export default RestDayDetail;
